// v2 Analytics — inline SVG charts.
// • Cast growth: new unique supporters per day (last 30d)
// • Tips by faction: donut
// • Tip distribution: histogram of donation amounts
// • Top supporters: lifetime leaderboard (top 10)
// • AI character generations: TODO until character_prompt is exposed

import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Navigate } from "react-router-dom";
import { useSession } from "../../lib/session";
import { api } from "../../lib/api";
import { aiCharImage, normHandle } from "../../lib/cast";
import "./dashboard.css";

interface DonationRow {
  id: string;
  donor_name: string;
  donor_handle?: string;
  amount: number;
  message: string | null;
  faction_id: string | null;
  created_at: number;
}
interface Faction {
  id: string;
  slug: string;
  name: string;
  color: string;
  total: number;
}
interface ProfileResp {
  factions: Faction[];
  donations: DonationRow[];
}

const DAY = 24 * 60 * 60 * 1000;

function dayKey(ts: number): string {
  const d = new Date(ts);
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(d.getUTCDate()).padStart(2, "0")}`;
}
function shortDay(ts: number): string {
  return new Date(ts).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

interface SeriesPoint { ts: number; value: number; }

function LineChart({ data, height = 180, width = 720 }: { data: SeriesPoint[]; height?: number; width?: number }): JSX.Element {
  // Pad/scale.
  const pad = { top: 14, right: 14, bottom: 26, left: 32 };
  const innerW = width - pad.left - pad.right;
  const innerH = height - pad.top - pad.bottom;
  const max = Math.max(1, ...data.map((d) => d.value));
  const n = Math.max(1, data.length - 1);
  const xAt = (i: number) => pad.left + (i / n) * innerW;
  const yAt = (v: number) => pad.top + innerH - (v / max) * innerH;
  const pts = data.map((d, i) => `${xAt(i)},${yAt(d.value)}`).join(" ");
  const areaPath = [
    "M", `${pad.left},${pad.top + innerH}`,
    ...data.map((d, i) => `L ${xAt(i)},${yAt(d.value)}`),
    `L ${pad.left + innerW},${pad.top + innerH}`,
    "Z",
  ].join(" ");
  // Y ticks.
  const ticks = 4;
  return (
    <svg className="dash-chart" viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="xMidYMid meet" role="img">
      <g className="dash-chart-grid">
        {Array.from({ length: ticks + 1 }, (_, i) => {
          const y = pad.top + (i / ticks) * innerH;
          return <line key={i} x1={pad.left} x2={pad.left + innerW} y1={y} y2={y} />;
        })}
      </g>
      <g className="dash-chart-axis">
        {Array.from({ length: ticks + 1 }, (_, i) => {
          const y = pad.top + (i / ticks) * innerH;
          const value = Math.round((max * (ticks - i)) / ticks);
          return (
            <text key={i} x={pad.left - 6} y={y + 3} textAnchor="end">{value}</text>
          );
        })}
        {data.length > 1 ? (
          <>
            <text x={pad.left} y={height - 6} textAnchor="start">{shortDay(data[0].ts)}</text>
            <text x={pad.left + innerW} y={height - 6} textAnchor="end">{shortDay(data[data.length - 1].ts)}</text>
          </>
        ) : null}
      </g>
      <path d={areaPath} className="dash-chart-area" />
      <polyline points={pts} className="dash-chart-line" />
      {data.map((d, i) => (
        <circle key={i} cx={xAt(i)} cy={yAt(d.value)} r={data.length > 30 ? 1.5 : 2.5} className="dash-chart-dot">
          <title>{shortDay(d.ts)}: {d.value}</title>
        </circle>
      ))}
    </svg>
  );
}

interface DonutSlice { id: string; name: string; color: string; value: number; }

function Donut({ slices, size = 200 }: { slices: DonutSlice[]; size?: number }): JSX.Element {
  const total = slices.reduce((s, x) => s + x.value, 0);
  if (total <= 0) {
    return (
      <svg className="dash-chart" viewBox={`0 0 ${size} ${size}`} width={size} height={size}>
        <circle cx={size / 2} cy={size / 2} r={size / 2 - 18} fill="none" stroke="var(--line)" strokeWidth={28} />
        <text x={size / 2} y={size / 2 + 5} textAnchor="middle" fill="var(--text-mute)" fontFamily="var(--f-mono)" fontSize={11}>NO DATA</text>
      </svg>
    );
  }
  const r = size / 2 - 18;
  const cx = size / 2;
  const cy = size / 2;
  const circ = 2 * Math.PI * r;
  let acc = 0;
  return (
    <svg className="dash-chart" viewBox={`0 0 ${size} ${size}`} width={size} height={size}>
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="var(--line)" strokeWidth={28} />
      {slices.map((s) => {
        const len = (s.value / total) * circ;
        const dash = `${len} ${circ - len}`;
        const offset = circ * 0.25 - acc; // start at top
        acc += len;
        return (
          <circle
            key={s.id}
            cx={cx}
            cy={cy}
            r={r}
            fill="none"
            stroke={s.color}
            strokeWidth={28}
            strokeDasharray={dash}
            strokeDashoffset={offset}
            transform={`rotate(-90 ${cx} ${cy})`}
            style={{ filter: `drop-shadow(0 0 4px ${s.color}66)` }}
          >
            <title>{s.name}: ${s.value.toLocaleString(undefined, { maximumFractionDigits: 0 })}</title>
          </circle>
        );
      })}
      <text x={cx} y={cy - 4} textAnchor="middle" fill="var(--text)" fontFamily="var(--f-display)" fontWeight={800} fontSize={20}>
        ${total.toLocaleString(undefined, { maximumFractionDigits: 0 })}
      </text>
      <text x={cx} y={cy + 14} textAnchor="middle" fill="var(--text-mute)" fontFamily="var(--f-mono)" fontSize={10} letterSpacing={2}>TOTAL</text>
    </svg>
  );
}

export default function DashboardAnalytics(): JSX.Element {
  const user = useSession((s) => s.user);
  const loading = useSession((s) => s.loading);

  const q = useQuery({
    queryKey: ["dashboard-analytics", user?.handle],
    queryFn: () => api.get<ProfileResp>(`/api/streamer/${user?.handle}`),
    enabled: !!user?.handle,
  });

  const donations = q.data?.donations ?? [];
  const factions = q.data?.factions ?? [];

  // --- Cast growth over time (new unique supporters per day, last 30) ---
  const growth = useMemo<SeriesPoint[]>(() => {
    const firstSeen = new Map<string, number>(); // handle -> first ts
    for (const d of donations) {
      const h = normHandle(d.donor_handle ?? d.donor_name);
      const prev = firstSeen.get(h);
      if (prev === undefined || d.created_at < prev) firstSeen.set(h, d.created_at);
    }
    const now = Date.now();
    const start = now - 30 * DAY;
    // Group new supporters by their first-seen day.
    const counts = new Map<string, number>();
    for (const ts of firstSeen.values()) {
      if (ts < start) continue;
      const k = dayKey(ts);
      counts.set(k, (counts.get(k) ?? 0) + 1);
    }
    const points: SeriesPoint[] = [];
    for (let i = 29; i >= 0; i--) {
      const dayTs = now - i * DAY;
      const k = dayKey(dayTs);
      points.push({ ts: dayTs, value: counts.get(k) ?? 0 });
    }
    return points;
  }, [donations]);

  // --- Tips by faction donut ---
  const donutSlices = useMemo<DonutSlice[]>(() => {
    return factions
      .filter((f) => f.total > 0)
      .map((f) => ({ id: f.id, name: f.name, color: f.color, value: f.total }))
      .sort((a, b) => b.value - a.value);
  }, [factions]);

  // --- Tip distribution histogram ---
  const histogram = useMemo(() => {
    const buckets = [
      { label: "$1–4",   min: 1,   max: 5 },
      { label: "$5–9",   min: 5,   max: 10 },
      { label: "$10–24", min: 10,  max: 25 },
      { label: "$25–49", min: 25,  max: 50 },
      { label: "$50–99", min: 50,  max: 100 },
      { label: "$100+",  min: 100, max: Infinity },
    ];
    const counts = buckets.map((b) => {
      const n = donations.filter((d) => d.amount >= b.min && d.amount < b.max).length;
      return { ...b, count: n };
    });
    const max = Math.max(1, ...counts.map((c) => c.count));
    return { counts, max };
  }, [donations]);

  // --- Top supporters ---
  const topSupporters = useMemo(() => {
    const tot = new Map<string, { name: string; handle: string; total: number; count: number }>();
    for (const d of donations) {
      const h = normHandle(d.donor_handle ?? d.donor_name);
      const ex = tot.get(h);
      if (ex) {
        ex.total += d.amount;
        ex.count += 1;
      } else {
        tot.set(h, { name: d.donor_name, handle: h, total: d.amount, count: 1 });
      }
    }
    return [...tot.values()].sort((a, b) => b.total - a.total).slice(0, 10);
  }, [donations]);

  // --- AI generations (TODO: needs character_prompt field exposed on API) ---
  const aiCount = useMemo(() => {
    // Heuristic stand-in: count donations with messages.
    return donations.filter((d) => d.message && d.message.trim().length > 0).length;
  }, [donations]);

  if (loading) return <p style={{ padding: 20 }}>Loading…</p>;
  if (!user) return <Navigate to="/login?returnUrl=/dashboard/analytics" replace />;

  return (
    <>
      <div className="dash-page-head">
        <div>
          <h1 className="dash-page-title">Analytics</h1>
          <p className="dash-page-sub">cast growth · faction split · tip distribution</p>
        </div>
      </div>

      {/* Cast growth */}
      <div className="dash-card elev">
        <div className="dash-card-head">
          <h2 className="dash-card-title">Cast growth (last 30 days)</h2>
          <span className="dash-card-sub">new supporters / day</span>
        </div>
        {donations.length === 0 ? (
          <p style={{ color: "var(--text-mute)", margin: 0 }}>No supporters yet.</p>
        ) : (
          <LineChart data={growth} />
        )}
      </div>

      {/* Tips by faction */}
      <div className="dash-card">
        <div className="dash-card-head">
          <h2 className="dash-card-title">Tips by faction</h2>
          <span className="dash-card-sub">all-time split</span>
        </div>
        <div className="dash-donut-row">
          <Donut slices={donutSlices} />
          <div className="dash-donut-legend">
            {donutSlices.length === 0 ? (
              <span style={{ color: "var(--text-mute)" }}>No faction tips yet.</span>
            ) : (
              donutSlices.map((s) => (
                <div key={s.id} className="dash-donut-legend-item">
                  <span className="dash-donut-legend-dot" style={{ background: s.color, color: s.color }} />
                  <span>{s.name}</span>
                  <span className="dash-donut-legend-amt">${s.value.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Tip distribution */}
      <div className="dash-card">
        <div className="dash-card-head">
          <h2 className="dash-card-title">Tip distribution</h2>
          <span className="dash-card-sub">amount buckets · count</span>
        </div>
        <div className="dash-hist-bars">
          {histogram.counts.map((b) => (
            <div
              key={b.label}
              className="dash-hist-bar"
              style={{ height: `${(b.count / histogram.max) * 100}%` }}
              title={`${b.label}: ${b.count}`}
            >
              <div style={{ position: "absolute", top: -18, left: 0, right: 0, textAlign: "center", fontFamily: "var(--f-mono)", fontSize: 11, color: "var(--text-sec)" }}>
                {b.count > 0 ? b.count : ""}
              </div>
            </div>
          ))}
        </div>
        <div className="dash-hist-labels">
          {histogram.counts.map((b) => (
            <div key={b.label} className="dash-hist-label">{b.label}</div>
          ))}
        </div>
      </div>

      {/* Top supporters */}
      <div className="dash-card">
        <div className="dash-card-head">
          <h2 className="dash-card-title">Top supporters</h2>
          <span className="dash-card-sub">lifetime · top 10</span>
        </div>
        {topSupporters.length === 0 ? (
          <p style={{ color: "var(--text-mute)", margin: 0 }}>No supporters yet.</p>
        ) : (
          <div className="dash-top-supp">
            {topSupporters.map((s, i) => (
              <div key={s.handle} className="dash-top-supp-row">
                <span className="dash-top-rank">#{i + 1}</span>
                <div className="dash-donor-thumb" style={{ width: 28, height: 28 }}>
                  <img src={aiCharImage(s.handle)} alt="" loading="lazy" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                </div>
                <div>
                  <div className="dash-donor-name" style={{ fontSize: 13 }}>{s.name}</div>
                  <div className="dash-donor-handle">@{s.handle} · {s.count} tip{s.count === 1 ? "" : "s"}</div>
                </div>
                <span className="dash-top-amt">${s.total.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* AI character generations */}
      <div className="dash-card">
        <div className="dash-card-head">
          <h2 className="dash-card-title">AI character generations</h2>
          <span className="dash-card-sub">available when AI tier enabled · TODO</span>
        </div>
        <div style={{ display: "flex", alignItems: "baseline", gap: 16 }}>
          <span style={{ fontFamily: "var(--f-display)", fontWeight: 800, fontSize: 36, color: "var(--gold)" }}>
            {aiCount}
          </span>
          <span style={{ fontFamily: "var(--f-mono)", fontSize: 11, color: "var(--text-mute)", letterSpacing: ".14em", textTransform: "uppercase" }}>
            est. · donations with prompts
          </span>
        </div>
        <p style={{ color: "var(--text-mute)", margin: "10px 0 0 0", fontSize: 12 }}>
          TODO: surface real <code style={{ fontFamily: "var(--f-mono)" }}>character_prompt</code> count from the donations API.
        </p>
      </div>
    </>
  );
}
